import { useCallback, useRef, useState } from 'react';
import { toast } from 'sonner';
import {
	type Attachment,
	type ImageAttachmentData,
	type DocumentAttachmentData,
	isSupportedImageType,
	isSupportedDocumentType,
	MAX_IMAGE_SIZE_BYTES,
	MAX_DOCUMENT_SIZE_BYTES,
	MAX_ATTACHMENTS_PER_MESSAGE,
	SUPPORTED_IMAGE_MIME_TYPES,
	SUPPORTED_DOCUMENT_MIME_TYPES,
} from '@/api-types';

export interface UseAttachmentUploadOptions {
	maxAttachments?: number;
	onError?: (error: string) => void;
}

export interface UseAttachmentUploadReturn {
	attachments: Attachment[];
	addFiles: (files: File[]) => Promise<void>;
	addUrlAttachment: (url: string, title: string, extractedText: string) => void;
	addIntegrationAttachment: (attachment: Attachment) => void;
	removeAttachment: (id: string) => void;
	clearAttachments: () => void;
	isProcessing: boolean;
}

/** MIME types accepted by the file input for images + documents */
export const ALL_ACCEPTED_MIME_TYPES = [...SUPPORTED_IMAGE_MIME_TYPES, ...SUPPORTED_DOCUMENT_MIME_TYPES];

/** Document-only MIME types for the document button */
export const DOCUMENT_ACCEPTED_MIME_TYPES = [...SUPPORTED_DOCUMENT_MIME_TYPES];

function generateId(prefix: string): string {
	return `${prefix}-${Date.now()}-${Math.random().toString(36).substring(2, 11)}`;
}

/**
 * Hook for handling unified attachment uploads (images, documents, URLs, integrations).
 * Generalizes use-image-upload.ts to support all attachment types.
 */
export function useAttachmentUpload(options: UseAttachmentUploadOptions = {}): UseAttachmentUploadReturn {
	const {
		maxAttachments = MAX_ATTACHMENTS_PER_MESSAGE,
		onError,
	} = options;

	const [attachments, setAttachments] = useState<Attachment[]>([]);
	const [isProcessing, setIsProcessing] = useState(false);
	const attachmentsCountRef = useRef(0);
	attachmentsCountRef.current = attachments.length;

	const processFile = useCallback(async (file: File): Promise<Attachment | null> => {
		const mimeType = file.type;

		// Image file
		if (isSupportedImageType(mimeType)) {
			if (file.size > MAX_IMAGE_SIZE_BYTES) {
				const msg = `Image too large: ${(file.size / (1024 * 1024)).toFixed(1)}MB. Max ${(MAX_IMAGE_SIZE_BYTES / (1024 * 1024)).toFixed(0)}MB.`;
				toast.error(msg);
				onError?.(msg);
				return null;
			}

			return new Promise((resolve, reject) => {
				const reader = new FileReader();
				reader.onload = (e) => {
					try {
						const result = e.target?.result as string;
						if (!result) { reject(new Error('Failed to read file')); return; }
						const base64Data = result.split(',')[1];

						const img = new Image();
						img.onload = () => {
							resolve({
								kind: 'image',
								id: generateId('img'),
								filename: file.name,
								mimeType: mimeType as ImageAttachmentData['mimeType'],
								base64Data,
								size: file.size,
								dimensions: { width: img.width, height: img.height },
							} satisfies ImageAttachmentData);
						};
						img.onerror = () => {
							resolve({
								kind: 'image',
								id: generateId('img'),
								filename: file.name,
								mimeType: mimeType as ImageAttachmentData['mimeType'],
								base64Data,
								size: file.size,
							} satisfies ImageAttachmentData);
						};
						img.src = result;
					} catch (error) {
						reject(error);
					}
				};
				reader.onerror = () => reject(new Error(`Failed to read file: ${file.name}`));
				reader.readAsDataURL(file);
			});
		}

		// Document file
		if (isSupportedDocumentType(mimeType)) {
			if (file.size > MAX_DOCUMENT_SIZE_BYTES) {
				const msg = `Document too large: ${(file.size / (1024 * 1024)).toFixed(1)}MB. Max ${(MAX_DOCUMENT_SIZE_BYTES / (1024 * 1024)).toFixed(0)}MB.`;
				toast.error(msg);
				onError?.(msg);
				return null;
			}

			return new Promise((resolve, reject) => {
				const reader = new FileReader();
				reader.onload = (e) => {
					try {
						const result = e.target?.result as string;
						if (!result) { reject(new Error('Failed to read file')); return; }
						const base64Data = result.split(',')[1];

						// For text-based formats, extract text client-side
						let extractedText: string | undefined;
						if (mimeType === 'text/plain' || mimeType === 'text/markdown' || mimeType === 'text/csv' || mimeType === 'application/json') {
							try {
								extractedText = atob(base64Data);
							} catch {
								// If atob fails, leave undefined for server-side extraction
							}
						}

						resolve({
							kind: 'document',
							id: generateId('doc'),
							filename: file.name,
							mimeType: mimeType as DocumentAttachmentData['mimeType'],
							base64Data,
							size: file.size,
							extractedText,
						} satisfies DocumentAttachmentData);
					} catch (error) {
						reject(error);
					}
				};
				reader.onerror = () => reject(new Error(`Failed to read file: ${file.name}`));
				reader.readAsDataURL(file);
			});
		}

		// Unsupported type
		const msg = `Unsupported file type: ${mimeType || 'unknown'}`;
		toast.error(msg);
		onError?.(msg);
		return null;
	}, [onError]);

	const addFiles = useCallback(async (files: File[]) => {
		setIsProcessing(true);
		try {
			if (attachmentsCountRef.current + files.length > maxAttachments) {
				const msg = `Maximum ${maxAttachments} attachments per message.`;
				toast.error(msg);
				onError?.(msg);
				return;
			}

			const processed = await Promise.all(files.map(f => processFile(f)));
			const valid = processed.filter((a): a is Attachment => a !== null);

			if (valid.length > 0) {
				setAttachments(prev => [...prev, ...valid]);
			}
		} catch (error) {
			onError?.(error instanceof Error ? error.message : 'Failed to process files');
		} finally {
			setIsProcessing(false);
		}
	}, [maxAttachments, processFile, onError]);

	const addUrlAttachment = useCallback((url: string, title: string, extractedText: string) => {
		if (attachmentsCountRef.current >= maxAttachments) {
			const msg = `Maximum ${maxAttachments} attachments per message.`;
			toast.error(msg);
			onError?.(msg);
			return;
		}

		setAttachments(prev => [...prev, {
			kind: 'url',
			id: generateId('url'),
			url,
			title,
			extractedText,
		}]);
	}, [maxAttachments, onError]);

	const addIntegrationAttachment = useCallback((attachment: Attachment) => {
		if (attachmentsCountRef.current >= maxAttachments) {
			const msg = `Maximum ${maxAttachments} attachments per message.`;
			toast.error(msg);
			onError?.(msg);
			return;
		}
		setAttachments(prev => [...prev, attachment]);
	}, [maxAttachments, onError]);

	const removeAttachment = useCallback((id: string) => {
		setAttachments(prev => prev.filter(a => a.id !== id));
	}, []);

	const clearAttachments = useCallback(() => {
		setAttachments([]);
	}, []);

	return {
		attachments,
		addFiles,
		addUrlAttachment,
		addIntegrationAttachment,
		removeAttachment,
		clearAttachments,
		isProcessing,
	};
}
